import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import CCOContainer from "../../fundamentals/CCOContainer";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import CCOParagraph from "../../fundamentals/CCOParagraph";
import {buttonStyle} from "../../../common/constants/buttonStyle";
import GiftCertificatePreview
    from "../../forms/F007ModularDonationForm/sections/F007GiftForm/GiftCertificatePreview";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import React, {useEffect} from "react";
import {useFormContext} from "react-hook-form";
import {DonationProductFormData} from "../../forms/F007ModularDonationForm/types";
import {useTranslation} from "react-i18next";
import {CmCollectionType, SosDonationProductType} from "../../types";
import C020TeaserCarousel from "../../content/C020TeaserCarousel/C020TeaserCarousel";
import {getCertificate, getQrCodeLink, ProductState} from "./P020DonationProduct";
import {redirectTo} from "../../../utility/navigationUtil";
import {fieldIntroSx, inputProps, wrapperSx} from "./P020DonationProductStyles";
import {
    CM_P_ID,
    NAME_PATTERN,
    PRODUCT_FIRSTNAME,
    PRODUCT_GREETING,
    PRODUCT_QUANTITY,
    PRODUCT_SURNAME,
    useSetDefaultValue,
    validProductDonation
} from "../../forms/F007ModularDonationForm/utils";
import {
    extractBinderText,
    extractProductBinderTree,
    getRelatedDonationProducts
} from "../../../utility/componentUtil";
import {useSessionContext} from "../../../context/SessionContext";
import {usePageContext} from "../../../context/PageContext";
import {FormInputTextField} from "../../forms/F007ModularDonationForm/inputs/FormInputTextField";
import {useRouter} from "next/router";
import CCOHeadlineAsText from "../../fundamentals/CCOHeadlineAsText";
import CCOLinkText from "../../fundamentals/CCOLinkText";

export interface Props {
    product: SosDonationProductType
}

export const SESSION_PARAM_PRODUCT_FORM_DATA = 'DonationProductFormData';
export const MAX_LENGTH_PRODUCT_GREETING = 365;

const getDefault = () => ({
    [PRODUCT_SURNAME]: '',
    [PRODUCT_FIRSTNAME]: '',
    [PRODUCT_GREETING]: '',
    [PRODUCT_QUANTITY]: 1,
});

function P020DonationProductForm(props: Props) {
    const router = useRouter();
    // Default Werte für das Formular
    const {watch} = useFormContext<DonationProductFormData>();
    const defaultSessionValues = getDefault();
    useSetDefaultValue(PRODUCT_QUANTITY, defaultSessionValues[PRODUCT_QUANTITY]);
    useSetDefaultValue(CM_P_ID, props.product.id);

    // Aus dem Pageconext werden die Labels und Einstellungen für das Formular geholt
    const {t} = useTranslation();
    const pageContext = usePageContext();
    const donationProductShopSettings = pageContext?.page?.donationProductShopSettings;
    const submitLabel = donationProductShopSettings?.donationProductPageSubmitLabel ||
        t('p020_product_label_Submit');
    const choseAmount = donationProductShopSettings?.donationProductPageChoseAmountLabel ||
        t("p020_product_amount");
    const formHeadline = donationProductShopSettings?.donationProductPageFormHeadline ||
        t('p020_product_form_headline');
    const greetingsLabel = donationProductShopSettings?.donationProductPageGreetingsLabel ||
        t("p020_product_greeting");
    const gifRecipientLabel = donationProductShopSettings?.donationProductPageGiftRecipientLabel ||
        t("p020_product_gifted");
    const backToOverviewLink = donationProductShopSettings?.donationProductBackToOverviewLink || '';
    const backToOverviewLabel = t('p020_product_back_to_overview');
    // In der Session merken wir uns beim Submit die Formulardaten, damit sie beim Rücksprung wieder vorausgefüllt sind
    const {setValue, handleSubmit} = useFormContext<DonationProductFormData>();
    const session = useSessionContext();
    // QR Code und Zertifikat
    const qrCodeLink = getQrCodeLink(props.product);
    const certificate = getCertificate(props.product);
    const productQuantity = watch(PRODUCT_QUANTITY) as number;
    const productFirstname = watch(PRODUCT_FIRSTNAME);
    const productSurname = watch(PRODUCT_SURNAME);
    const productGreeting = watch(PRODUCT_GREETING);
    const isValid = validProductDonation(productQuantity, productFirstname, productSurname,
        productGreeting);
    const naturalPositiveNumber = new RegExp('^[1-9][0-9]*$');
    // Binder helpers are now centralized in utility/componentUtil.ts

    const productBinderTemplate = extractBinderText(props.product?.binder?.[0]);
    const binderTextAsTreeForView = extractProductBinderTree(props.product);
    const relatedProducts = getRelatedDonationProducts(props.product);


    // Zum prefill des Formulars, wenn man vom Spendenformular zurückkommt
    useEffect(() => {
        const storedData = session.getJsonFromSession(
            SESSION_PARAM_PRODUCT_FORM_DATA) as ProductState;
        if (storedData) {
            const greeting = storedData.productGreeting ? storedData.productGreeting : '';
            const firstName = storedData.productFirstname ? storedData.productFirstname : '';
            const surname = storedData.productSurname ? storedData.productSurname : '';
            const quantity = storedData.productQuantity ? storedData.productQuantity : 1;
            setValue(PRODUCT_GREETING, greeting);
            setValue(PRODUCT_FIRSTNAME, firstName);
            setValue(PRODUCT_SURNAME, surname);
            setValue(PRODUCT_QUANTITY, quantity);
        }
        // eslint-disable-next-line
    }, []);

    // Falls der User die Seite verlässt, wird die eingegebene Menge auf 1 gesetzt
    useEffect(() => {
        const handleRouteChange = () => {
            const storedData = session.getJsonFromSession(
                SESSION_PARAM_PRODUCT_FORM_DATA) as ProductState;
            const state = storedData ? storedData : defaultSessionValues;
            state.productQuantity = 1;
            session.addJsonToSession(SESSION_PARAM_PRODUCT_FORM_DATA, state);
        };
        router.events.on("routeChangeStart", handleRouteChange);
        return () => {
            router.events.off("routeChangeStart", handleRouteChange);
        }
        // eslint-disable-next-line
    }, [router]);

    /**
     * Erstellt die Weiterleitungs-URL
     * @param forwardUrl
     */
    function getForwardUrl(forwardUrl: string) {
        const parameters = new URLSearchParams();
        parameters.append(CM_P_ID, props.product.id);
        return forwardUrl + '?' + parameters.toString();
    }


    function onQuantityChange(event: any) {
        const newQuantity = event.target.value;
        if (naturalPositiveNumber.test(newQuantity) || newQuantity === "") {
            setValue(PRODUCT_QUANTITY, newQuantity);
        } else {
            // Bei ungültiger Eingabe wird der Defaultwert gesetzt
            setValue(PRODUCT_QUANTITY, defaultSessionValues[PRODUCT_QUANTITY]);
        }
    }

    const onSubmit = async () => {
        const forwardUrl = getForwardUrl(
            donationProductShopSettings?.donationProductDonationFormLink || '');
        if (validProductDonation(productQuantity, productFirstname, productSurname,
            productGreeting)) {
            const state = {
                productQuantity: productQuantity,
                productGreeting: productGreeting,
                productFirstname: productFirstname,
                productSurname: productSurname
            } as ProductState;
            session.addJsonToSession(SESSION_PARAM_PRODUCT_FORM_DATA, state);
            redirectTo(forwardUrl);
        }
    };

    const relatedProductsCarousel = relatedProducts.length > 0 ? (
        <CCOComponentWrapper background='grey' dataCypress='teaser-carousel'>
            <CCOContainer background='grey' width='large' marginBottom={0}>
                <CCOContainer width='large' marginBottom={6} marginTop={6}>
                    <C020TeaserCarousel
                        cmCollection={{
                            id: `${props.product.id}-related-products`,
                            items: relatedProducts
                        } as CmCollectionType}
                        headline={t('p020_product_related_headline')!}
                        renderInline
                    />
                </CCOContainer>
            </CCOContainer>
        </CCOComponentWrapper>
    ) : null;

    return (
        <>
        <form data-cypress='donationProductForm' method='GET'>
            <CCOComponentWrapper>
                <CCOContainer width='large'>
                    <CCOContainer>
                        <Grid container spacing={4} columns={8} sx={{
                            alignItems: 'center'
                        }}>
                            <Grid size={{xs: 8, md: 6, lg: 6}}>
                                <CCOHeadlineAsText textAlign='left' title={choseAmount} variant='h4'/>
                            </Grid>
                            <Grid size={{xs: 8, md: 2, lg: 2}}>
                                <FormInputTextField
                                    name={PRODUCT_QUANTITY}
                                    type='number'
                                    inputSlotProps={inputProps}
                                    label=''
                                    sx={wrapperSx}
                                    pattern={naturalPositiveNumber}
                                    onChange={onQuantityChange}
                                    required
                                />
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}} sx={{
                                marginTop: 2
                            }}>
                                <CCOHeadlineAsText title={formHeadline}
                                                   textAlign='left' variant='h4'/>
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}}>
                                <CCOParagraph sx={fieldIntroSx}>{gifRecipientLabel!}</CCOParagraph>
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}}>
                                <FormInputTextField
                                    name={PRODUCT_FIRSTNAME}
                                    label=''
                                    placeholder={t('p020_product_label_firstName')!}
                                    required={true}
                                    pattern={NAME_PATTERN}
                                />
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}}>
                                <FormInputTextField
                                    name={PRODUCT_SURNAME}
                                    label=''
                                    placeholder={t('p020_product_label_lastName')!}
                                    required={true}
                                    pattern={NAME_PATTERN}
                                />
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}}>
                                <CCOParagraph sx={fieldIntroSx}>{greetingsLabel!}</CCOParagraph>
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}} sx={{
                                marginBottom: 3
                            }}>
                                <FormInputTextField
                                    name={PRODUCT_GREETING}
                                    maxLength={MAX_LENGTH_PRODUCT_GREETING}
                                    label=''
                                    placeholder={t('p020_product_label_greeting')!}
                                />
                            </Grid>
                            <Grid
                                size={{xs: 8, md: 4, lg: 4}}
                                sx={{
                                    marginTop: 2,
                                    marginBottom: 2
                                }}>
                                <GiftCertificatePreview pdfCertificate={certificate!}
                                                        qrCodeLink={qrCodeLink || undefined}
                                                        isValid={!!isValid}
                                                        isProduct
                                                        productBinderTemplate={productBinderTemplate}
                                />
                            </Grid>
                            <Grid
                                size={{xs: 8, md: 4, lg: 4}}
                                sx={{
                                    marginTop: 2,
                                    marginBottom: 2
                                }}>
                                <Button type='button'
                                        size='large'
                                        variant='contained'
                                        onClick={handleSubmit(onSubmit)}
                                        disabled={!isValid}
                                        data-cypress='product-donation-submit'
                                        sx={{...buttonStyle.green}}
                                        fullWidth>{submitLabel}</Button>
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}}>
                                <Divider/>
                            </Grid>
                            <Grid size={{xs: 8, md: 8, lg: 8}}>
                                <Grid container spacing={2} columns={8}>
                                    <Grid size={{xs: 8}}>
                                        <CCORichText metadataId={props.product.id}
                                                     textAsTree={props.product.details?.textAsTree}
                                                     propertyName='properties.details'/>
                                    </Grid>
                                    {binderTextAsTreeForView && (
                                        <Grid size={{xs: 8}}>
                                            <CCORichText
                                                metadataId={props.product.id}
                                                textAsTree={binderTextAsTreeForView}
                                                propertyName={'properties.binder'}
                                            />
                                        </Grid>
                                    )}
                                    {backToOverviewLink && (
                                        <Grid size={{xs: 8}}>
                                            <a href={backToOverviewLink}
                                               data-cypress='product-back-to-overview-link'>
                                                <CCOLinkText txt={backToOverviewLabel}/>
                                            </a>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </CCOContainer>
                </CCOContainer>
            </CCOComponentWrapper>
        </form>
            {relatedProductsCarousel}
        </>
    );
}

export default P020DonationProductForm;
